/**
 * Selection controls of the pricing screens (#3034). A user picks categories, parts and customers
 * by searching their names; nobody types an id. Part and customer searches are server searches that
 * page with an explicit "load more", so a large catalogue is never cut off after its first page.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { useDebounce } from '@/hooks/useDebounce';
import { usePricingPermissions, type PricingPartyKind } from '@/hooks/usePartPricing';
import {
  useScopedCategories,
  useScopedCompanies,
  useScopedCompany,
  useScopedPart,
  useScopedParts,
  useScopedPerson,
  useScopedPersons,
} from './pickerQueries';

export interface PickedPart {
  id: string;
  sku: string;
  name: string;
}

export interface PickedParty {
  kind: PricingPartyKind;
  id: string;
  name: string;
}

const MAX_CATEGORY_DEPTH = 32;

interface CategoryNode {
  id: string;
  name: string;
  parentCategoryId: string | null;
}

/** `Parent › Child` path of a category; a broken or cyclic chain stops where it breaks. */
function categoryPath(id: string, byId: ReadonlyMap<string, CategoryNode>): string {
  const names: string[] = [];
  const seen = new Set<string>();
  let current = byId.get(id);
  while (current && !seen.has(current.id) && names.length < MAX_CATEGORY_DEPTH) {
    seen.add(current.id);
    names.unshift(current.name);
    current = current.parentCategoryId ? byId.get(current.parentCategoryId) : undefined;
  }
  return names.join(' › ');
}

/** Every category with its path, plus a lookup of a category's label. */
export function useCategoryOptions() {
  const query = useScopedCategories();
  return useMemo(() => {
    const nodes = query.data?.data ?? [];
    const byId = new Map(nodes.map((node) => [node.id, node] as const));
    const options = nodes
      .map((node) => ({ id: node.id, label: categoryPath(node.id, byId) }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return {
      options,
      labelOf: (id: string | null | undefined): string | null => (id ? (byId.has(id) ? categoryPath(id, byId) : null) : null),
      isLoading: query.isLoading,
      isError: query.isError,
    };
  }, [query.data, query.isLoading, query.isError]);
}

export function CategoryName({ id }: { id: string | null }) {
  const { t } = useTranslation('partPricing');
  const { labelOf, isLoading } = useCategoryOptions();
  if (!id) return <>{t('names.unknownCategory')}</>;
  return <>{labelOf(id) ?? (isLoading ? '…' : t('names.unknownCategory'))}</>;
}

export function PartName({ id }: { id: string | null }) {
  const { t } = useTranslation('partPricing');
  const part = useScopedPart(id ?? undefined);
  if (!id) return <>{t('names.unknownPart')}</>;
  if (part.data) return <>{`${part.data.sku} — ${part.data.name}`}</>;
  return <>{part.isLoading ? '…' : t('names.unknownPart')}</>;
}

/** The customer's name; without `customers.read` only the kind is known, never an id. */
export function PartyName({ kind, id }: { kind: PricingPartyKind; id: string }) {
  const { t } = useTranslation('partPricing');
  const { canReadCustomers } = usePricingPermissions();
  const company = useScopedCompany(canReadCustomers && kind === 'company' ? id : undefined);
  const person = useScopedPerson(canReadCustomers && kind === 'person' ? id : undefined);
  // The right is read now: a name already cached is not shown once `customers.read` is gone.
  if (!canReadCustomers) return <>{t(`names.unknownParty.${kind}`)}</>;
  const name =
    kind === 'company' ? company.data?.name : person.data ? `${person.data.firstName} ${person.data.lastName}`.trim() : undefined;
  if (name) return <>{name}</>;
  const loading = kind === 'company' ? company.isLoading : person.isLoading;
  return <>{loading ? '…' : t(`names.unknownParty.${kind}`)}</>;
}

// ---------------------------------------------------------------------------
// Shared pieces
// ---------------------------------------------------------------------------

interface SearchItem {
  id: string;
  primary: string;
  secondary?: string | undefined;
}

interface SearchResultsProps {
  label: string;
  search: string;
  onSearchChange: (value: string) => void;
  items: SearchItem[];
  isLoading: boolean;
  isError: boolean;
  isFetchingMore: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  onPick: (id: string) => void;
  disabled?: boolean | undefined;
}

function SearchResults({
  label,
  search,
  onSearchChange,
  items,
  isLoading,
  isError,
  isFetchingMore,
  hasMore,
  onLoadMore,
  onPick,
  disabled,
}: SearchResultsProps) {
  const { t } = useTranslation('partPricing');
  const { t: tc } = useTranslation('common');
  return (
    <Stack spacing={1}>
      <TextField
        size="small"
        label={label}
        value={search}
        disabled={disabled}
        onChange={(event) => onSearchChange(event.target.value)}
        helperText={t('picker.searchHint')}
        slotProps={{
          input: { endAdornment: isLoading ? <CircularProgress size={18} /> : null },
        }}
      />
      {isError && <Alert severity="error">{t('picker.loadFailed')}</Alert>}
      {!isLoading && !isError && items.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {t('picker.noResults')}
        </Typography>
      )}
      {items.length > 0 && (
        <List dense disablePadding sx={{ maxHeight: 240, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
          {items.map((item) => (
            <ListItemButton key={item.id} disabled={disabled} onClick={() => onPick(item.id)}>
              <ListItemText primary={item.primary} secondary={item.secondary} />
            </ListItemButton>
          ))}
        </List>
      )}
      {hasMore && (
        <Box>
          <Button size="small" onClick={onLoadMore} disabled={disabled || isFetchingMore}>
            {isFetchingMore ? tc('loading') : tc('loadMore')}
          </Button>
        </Box>
      )}
    </Stack>
  );
}

function SelectedEntity({
  label,
  value,
  onClear,
  disabled,
}: {
  label: string;
  value: ReactNode;
  onClear: () => void;
  disabled?: boolean | undefined;
}) {
  const { t: tc } = useTranslation('common');
  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" display="block">
          {label}
        </Typography>
        <Typography variant="body1" noWrap>
          {value}
        </Typography>
      </Box>
      {!disabled && (
        <Tooltip title={tc('clear')}>
          <IconButton size="small" aria-label={tc('clear')} onClick={onClear}>
            <ClearIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Category
// ---------------------------------------------------------------------------

interface CategoryPickerProps {
  label: string;
  value: string | null;
  onChange: (categoryId: string | null) => void;
  disabled?: boolean | undefined;
  helperText?: string | undefined;
  /** Categories that cannot be chosen (e.g. already have a rule elsewhere). */
  isOptionDisabled?: ((categoryId: string) => boolean) | undefined;
}

/** All categories (the endpoint returns the whole tree) with type-ahead filtering by path. */
export function CategoryPicker({ label, value, onChange, disabled, helperText, isOptionDisabled }: CategoryPickerProps) {
  const { t } = useTranslation('partPricing');
  const { options, isLoading, isError } = useCategoryOptions();
  const selected = options.find((option) => option.id === value) ?? null;
  return (
    <Autocomplete
      size="small"
      options={options}
      value={selected}
      disabled={disabled}
      loading={isLoading}
      loadingText={t('picker.loading')}
      noOptionsText={t('picker.noResults')}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, current) => option.id === current.id}
      getOptionDisabled={(option) => isOptionDisabled?.(option.id) ?? false}
      onChange={(_event, option) => onChange(option?.id ?? null)}
      renderInput={(params) => (
        <TextField {...params} label={label} error={isError} helperText={isError ? t('picker.loadFailed') : helperText} />
      )}
    />
  );
}

// ---------------------------------------------------------------------------
// Part
// ---------------------------------------------------------------------------

interface PartPickerProps {
  label: string;
  value: PickedPart | null;
  onChange: (part: PickedPart | null) => void;
  disabled?: boolean | undefined;
}

/** Server search of the catalogue by SKU, name or keyword; pages with "load more". */
export function PartPicker({ label, value, onChange, disabled }: PartPickerProps) {
  const [search, setSearch] = useState('');
  const query = useDebounce(search.trim(), 250);
  const parts = useScopedParts({ ...(query ? { q: query } : {}), limit: 20 }, { enabled: value === null });
  const items = useMemo(
    () =>
      (parts.data?.pages.flatMap((page) => page.data) ?? []).map((part) => ({
        id: part.id,
        primary: part.name,
        secondary: part.sku,
        sku: part.sku,
      })),
    [parts.data],
  );

  if (value) {
    return (
      <SelectedEntity
        label={label}
        value={`${value.sku} — ${value.name}`}
        disabled={disabled}
        onClear={() => {
          setSearch('');
          onChange(null);
        }}
      />
    );
  }
  return (
    <SearchResults
      label={label}
      search={search}
      onSearchChange={setSearch}
      items={items}
      isLoading={parts.isLoading || (search.trim() !== query && search.trim() !== '')}
      isError={parts.isError}
      isFetchingMore={parts.isFetchingNextPage}
      hasMore={parts.hasNextPage}
      onLoadMore={() => void parts.fetchNextPage()}
      disabled={disabled}
      onPick={(id) => {
        const part = items.find((item) => item.id === id);
        if (part) onChange({ id: part.id, sku: part.sku, name: part.primary });
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Customer (company OR person)
// ---------------------------------------------------------------------------

interface PartyPickerProps {
  label: string;
  value: PickedParty | null;
  onChange: (party: PickedParty | null) => void;
  disabled?: boolean | undefined;
}

/** A company or a person from the registry, searched by name, with "load more". */
export function PricingPartyPicker({ label, value, onChange, disabled }: PartyPickerProps) {
  const { t } = useTranslation('partPricing');
  const { canReadCustomers } = usePricingPermissions();
  const [kind, setKind] = useState<PricingPartyKind>('company');
  const [search, setSearch] = useState('');
  const query = useDebounce(search.trim(), 250);
  const filters = query ? { q: query, limit: 20 } : { limit: 20 };
  const searching = canReadCustomers && value === null;
  const companies = useScopedCompanies(filters, { enabled: searching && kind === 'company' });
  const persons = useScopedPersons(filters, { enabled: searching && kind === 'person' });

  const items = useMemo(() => {
    if (kind === 'company') {
      return (companies.data?.pages.flatMap((page) => page.data) ?? []).map((company) => ({
        id: company.id,
        primary: company.name,
        secondary: [company.businessId, company.city].filter(Boolean).join(' · ') || undefined,
      }));
    }
    return (persons.data?.pages.flatMap((page) => page.data) ?? []).map((person) => ({
      id: person.id,
      primary: `${person.firstName} ${person.lastName}`.trim(),
      secondary: [person.phone, person.email].filter(Boolean).join(' · ') || undefined,
    }));
  }, [kind, companies.data, persons.data]);

  if (value) {
    return (
      <SelectedEntity
        label={label}
        // Without `customers.read` (now) the chosen customer is shown by kind only, never by name.
        value={canReadCustomers ? `${t(`partyKind.${value.kind}`)}: ${value.name}` : t(`partyKind.${value.kind}`)}
        disabled={disabled}
        onClear={() => {
          setSearch('');
          onChange(null);
        }}
      />
    );
  }
  if (!canReadCustomers) return <Alert severity="info">{t('picker.customersNeedPermission')}</Alert>;

  const active = kind === 'company' ? companies : persons;
  return (
    <Stack spacing={1}>
      <ToggleButtonGroup
        exclusive
        fullWidth
        size="small"
        value={kind}
        disabled={disabled}
        aria-label={label}
        onChange={(_event, next: PricingPartyKind | null) => {
          if (next) {
            setKind(next);
            setSearch('');
          }
        }}
      >
        <ToggleButton value="company">{t('partyKind.company')}</ToggleButton>
        <ToggleButton value="person">{t('partyKind.person')}</ToggleButton>
      </ToggleButtonGroup>
      <SearchResults
        label={t(`picker.searchParty.${kind}`)}
        search={search}
        onSearchChange={setSearch}
        items={items}
        isLoading={active.isLoading}
        isError={active.isError}
        isFetchingMore={active.isFetchingNextPage}
        hasMore={active.hasNextPage}
        onLoadMore={() => void active.fetchNextPage()}
        disabled={disabled}
        onPick={(id) => {
          const picked = items.find((item) => item.id === id);
          if (picked) onChange({ kind, id: picked.id, name: picked.primary });
        }}
      />
    </Stack>
  );
}
